import { useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faDownload } from '@fortawesome/free-solid-svg-icons';
import { publicRequestService } from '../../services/api';
import { downloadPublicReceipt, statusToLabel } from '../../services/receiptPdf';
import type { TrackByProtocolResponse, TrackByCPFItem } from '../../types/publicRequest';
import { ORDER_STATUS_LABELS } from '../../types/publicRequest';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAADi1mqWEGyGXPcDO';

type Tab = 'protocol' | 'cpf';

export default function TrackRequest() {
  const [tab, setTab] = useState<Tab>('protocol');
  const [protocol, setProtocol] = useState('');
  const [cpf, setCpf] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultSingle, setResultSingle] = useState<TrackByProtocolResponse | null>(null);
  const [resultList, setResultList] = useState<TrackByCPFItem[] | null>(null);
  const turnstileToken = useRef<string | null>(null);
  const protoWidgetId = useRef<string | undefined>(undefined);
  const cpfWidgetId = useRef<string | undefined>(undefined);

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, '').slice(0, 11);
    if (val.length > 9) val = val.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    else if (val.length > 6) val = val.replace(/^(\d{3})(\d{3})(\d{3})/, '$1.$2.$3-');
    else if (val.length > 3) val = val.replace(/^(\d{3})(\d{3})/, '$1.$2');
    setCpf(val);
  };

  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, '').slice(0, 8);
    if (val.length > 4) val = val.replace(/^(\d{2})(\d{2})(\d{0,4})/, '$1/$2/$3');
    else if (val.length > 2) val = val.replace(/^(\d{2})(\d{0,2})/, '$1/$2');
    setBirthDate(val);
  };

  const searchByProtocol = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null); setResultList(null); setResultSingle(null);
    if (!protocol.trim()) { setError('Informe o número de protocolo.'); return; }
    if (!turnstileToken.current) { setError('Verificação anti-robô pendente.'); return; }
    setLoading(true);
    try {
      const res = await publicRequestService.trackByProtocol({ protocolNumber: protocol.trim().toUpperCase(), turnstileToken: turnstileToken.current });
      setResultSingle(res); turnstileToken.current = null;
      window.turnstile?.reset(protoWidgetId.current ?? undefined);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Falha ao consultar.');
      window.turnstile?.reset(protoWidgetId.current ?? undefined); turnstileToken.current = null;
    } finally { setLoading(false); }
  };

  const searchByCPF = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null); setResultSingle(null); setResultList(null);
    const digits = cpf.replace(/\D/g, '');
    if (digits.length !== 11) { setError('CPF inválido.'); return; }
    if (birthDate.length < 10) { setError('Data de nascimento obrigatória.'); return; }
    // Converte DD/MM/AAAA → YYYY-MM-DD para o backend
    const birthDateISO = birthDate.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$3-$2-$1');
    if (!turnstileToken.current) { setError('Verificação anti-robô pendente.'); return; }
    setLoading(true);
    try {
      const res = await publicRequestService.trackByCPF({ cpf: digits, birthDate: birthDateISO, turnstileToken: turnstileToken.current });
      setResultList(res.items); turnstileToken.current = null;
      window.turnstile?.reset(cpfWidgetId.current ?? undefined);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Falha ao consultar.');
      window.turnstile?.reset(cpfWidgetId.current ?? undefined); turnstileToken.current = null;
    } finally { setLoading(false); }
  };

  const renderProtoTurnstile = useCallback((el: HTMLDivElement | null) => {
    if (!el || !TURNSTILE_SITE_KEY) return;
    protoWidgetId.current = window.turnstile?.render(el, { sitekey: TURNSTILE_SITE_KEY, callback: (token: string) => { turnstileToken.current = token; } });
  }, []);

  const renderCpfTurnstile = useCallback((el: HTMLDivElement | null) => {
    if (!el || !TURNSTILE_SITE_KEY) return;
    cpfWidgetId.current = window.turnstile?.render(el, { sitekey: TURNSTILE_SITE_KEY, callback: (token: string) => { turnstileToken.current = token; } });
  }, []);

  const statusBadgeStyle = (status: string): React.CSSProperties => {
    switch (status) {
      case 'ABERTA': return { color: 'var(--color-badge-aberta-text)', background: 'var(--color-badge-aberta-bg)' };
      case 'AVULSA': return { color: 'var(--color-badge-aberta-text)', background: 'var(--color-badge-aberta-bg)' };
      case 'EM_ANALISE': return { color: '#6741d9', background: '#f3f0ff' };
      case 'EM_ANDAMENTO': return { color: 'var(--color-badge-andamento-text)', background: 'var(--color-badge-andamento-bg)' };
      case 'PENDENTE': return { color: 'var(--color-badge-pendente-text)', background: 'var(--color-badge-pendente-bg)' };
      case 'CONCLUIDA': return { color: 'var(--color-badge-concluida-text)', background: 'var(--color-badge-concluida-bg)' };
      case 'EM_ATRASO': return { color: 'var(--color-badge-atraso-text)', background: 'var(--color-badge-atraso-bg)' };
      default: return { color: '#495057', background: '#e9ecef' };
    }
  };

  const formatDate = (iso: string): string => new Date(iso).toLocaleDateString('pt-BR');

  const handleDownloadReceipt = (r: TrackByProtocolResponse | TrackByCPFItem) => {
    downloadPublicReceipt({
      protocolNumber: r.protocolNumber,
      reporterName: r.reporterName || '',
      address: r.address,
      neighborhood: r.neighborhood,
      poleBusbar: r.poleBusbar || '',
      status: r.orderStatus,
      statusLabel: statusToLabel(r.orderStatus),
      openedAt: r.openedAt || r.createdAt,
    });
  };

  const renderSingleResult = (r: TrackByProtocolResponse) => (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginBottom: 16 }}>Status da ordem de serviço</h3>
      <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
        <div><strong>Protocolo:</strong> {r.protocolNumber}</div>
        {r.reporterName && <div><strong>Solicitante:</strong> {r.reporterName}</div>}
        <div><strong>Status:</strong>{' '}
          <span className="status-badge" style={statusBadgeStyle(r.orderStatus)}>{ORDER_STATUS_LABELS[r.orderStatus] || r.orderStatus}</span>
        </div>
        {r.poleBusbar && <div><strong>Barramento:</strong> {r.poleBusbar}</div>}
        <div><strong>Endereço:</strong> {r.address}</div>
        <div><strong>Bairro:</strong> {r.neighborhood}</div>
        <div><strong>Data:</strong> {formatDate(r.openedAt || r.createdAt)}</div>
      </div>
      <button className="btn btn-primary btn-block" onClick={() => handleDownloadReceipt(r)} style={{ marginTop: 14 }}>
        <FontAwesomeIcon icon={faDownload} /> Baixar comprovante
      </button>
    </div>
  );

  const renderListResults = (items: TrackByCPFItem[]) => (
    <div style={{ marginTop: 16 }}>
      <h3 style={{ marginBottom: 12 }}>{items.length} ordem(ns) de serviço encontrada{items.length === 1 ? '' : 's'}</h3>
      {items.map((item) => (
        <div key={item.protocolNumber} className="card" style={{ marginBottom: 12, padding: '16px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ fontSize: 14 }}>{item.protocolNumber}</strong>
            <span className="status-badge" style={statusBadgeStyle(item.orderStatus)}>{ORDER_STATUS_LABELS[item.orderStatus] || item.orderStatus}</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 10 }}>
            {item.reporterName && <span>{item.reporterName} · </span>}
            {item.address} · {item.neighborhood} · {formatDate(item.openedAt || item.createdAt)}
          </div>
          <button className="btn btn-primary" onClick={() => handleDownloadReceipt(item)} style={{ fontSize: 13, padding: '8px 16px' }}>
            <FontAwesomeIcon icon={faDownload} /> Comprovante
          </button>
        </div>
      ))}
    </div>
  );

  const showResults = resultSingle || (resultList && resultList.length > 0);
  const showEmptyList = resultList !== null && resultList.length === 0;

  return (
    <div>
      <div className="tabs">
        <button className={`tab ${tab === 'protocol' ? 'active' : ''}`} onClick={() => setTab('protocol')}>Por protocolo</button>
        <button className={`tab ${tab === 'cpf' ? 'active' : ''}`} onClick={() => setTab('cpf')}>Por CPF + data de nascimento</button>
      </div>
      <div className="card" style={{ padding: '24px 20px' }}>
        {tab === 'protocol' ? (
          <form onSubmit={searchByProtocol}>
            <div className="form-group">
              <label>Número de protocolo</label>
              <input value={protocol} onChange={(e) => setProtocol(e.target.value.toUpperCase())} placeholder="ILUM-2026-XXXXXX-X" maxLength={50} />
            </div>
            {TURNSTILE_SITE_KEY && (<div className="form-group" style={{ display: 'flex', justifyContent: 'center' }}><div ref={renderProtoTurnstile} /></div>)}
            <button className="btn btn-primary btn-block" type="submit" disabled={loading}>{loading && <span className="spinner" />}<FontAwesomeIcon icon={faSearch} /> Consultar</button>
          </form>
        ) : (
          <form onSubmit={searchByCPF}>
            <div className="form-group"><label>CPF</label><input value={cpf} onChange={handleCpfChange} placeholder="000.000.000-00" inputMode="numeric" /></div>
            <div className="form-group"><label>Data de nascimento</label><input value={birthDate} onChange={handleBirthDateChange} placeholder="DD/MM/AAAA" maxLength={10} /></div>
            {TURNSTILE_SITE_KEY && (<div className="form-group" style={{ display: 'flex', justifyContent: 'center' }}><div ref={renderCpfTurnstile} /></div>)}
            <button className="btn btn-primary btn-block" type="submit" disabled={loading}>{loading && <span className="spinner" />}<FontAwesomeIcon icon={faSearch} /> Consultar</button>
          </form>
        )}
        {error && <div className="alert alert-error" style={{ marginTop: 16, marginBottom: 0 }}>{error}</div>}
      </div>
      {showEmptyList && (<div className="card" style={{ marginTop: 16, textAlign: 'center' }}><p style={{ color: 'var(--color-text-muted)' }}>Nenhuma solicitação encontrada para este CPF e data de nascimento.</p></div>)}
      {!showResults && !showEmptyList && (<p style={{ textAlign: 'center', color: 'var(--color-text-muted)', marginTop: 32, fontSize: 14 }}>Informe o número de protocolo, ou seu CPF e data de nascimento, e clique em Consultar.</p>)}
      {resultSingle && renderSingleResult(resultSingle)}
      {resultList && resultList.length > 0 && renderListResults(resultList)}
    </div>
  );
}
